import React from 'react';

import PackageContext from './PackageContext';

// Package components
import Ad from '../Ad';
import ALaCarte from './ALaCarte';
import Bacon from './Bacon';
import BigHeadline from './BigHeadline';
import BigStoryTakeover from './BigStoryTakeover';
import Collection from './Collection';
import CollectionLead from './CollectionLead';
import CollectionTitle from './CollectionTitle';
import CoverSpread from './CoverSpread/Default';
import { DynamicUp } from './DynamicUp';
import Embed from './Embed';
import ErrorBoundary from '../ErrorBoundary';
import GiftGuideWaffle from './Waffle/GiftGuideWaffle';
import LeadOneUp from './LeadOneUp';
import LeadSectionFront from './LeadSectionFront';
import LiveBlog from './LiveBlog';
import LiveBlogCoverSpread from './CoverSpread/LiveBlogCoverSpread';
import LiveVideoPromo from './LiveVideoPromo';
import LocalNews from './LocalNews';
import MagazineLead from './MagazineLead';
import Mosaic from './Mosaic';
import { MsnbcDaily } from '../MsnbcDaily';
import OneUp from './OneUp';
import Pancake from './Pancake';
import ProductWaffle from './Waffle/ProductWaffle';
import Promo3Panel from './Promo3Panel';
import Schedule from './Schedule';
import ShowLead from './ShowLead';
import SlimLead from './SlimLead';
import StandardLead from './StandardLead';
import StickyAdTrack from './StickyAdTrack';
import StraightUp from './StraightUp';
import Taboola from '../Taboola';
import TeaseList from './TeaseList';
import ThreeFeatured from './ThreeFeatured';
import ThreeFeaturedPlusList from './ThreeFeaturedPlusList';
import ThreeUp from './ThreeUp';
import TwoUp from './TwoUp';
import UpController from './UpController';
import VideoPackage from './VideoPackage';
import Waffle from './Waffle/Default';

function getPackage(packageData, props = {}, context = {}) {
  const { id, items, metadata = {} } = packageData;
  const {
    adsDisabled,
    isRailAdjacent,
    layoutIndex,
    pageRoute,
    railContext,
    shouldMirror,
    vertical,
  } = context;
  const { slot } = props;

  switch (packageData.type) {
    /** UP PACKAGES  */
    case 'oneUp':
      return (
        <OneUp
          standalone
          content={packageData}
          isRailAdjacent={isRailAdjacent}
          key={id}
          {...props}
          vertical={vertical}
          isFirstLayout={!layoutIndex}
        />
      );
    case 'twoUp':
    case 'threeUp':
    case 'fourUp':
      return <UpController package={packageData} adsDisabled={adsDisabled} {...props} />;
    case 'sevenUp':
      if (items.filter((x) => x).length === 0) {
        return null;
      }
      /* TODO: this will be gone when oneUp, twoUp, threeUp are used instead */
      /* note that subpackages don't need package spacing */
      return (
        <StickyAdTrack
          inFront
          mirror={shouldMirror()}
          title={metadata.title}
          text={metadata.seeAllText}
          url={metadata.seeAllUrl}
          logoUrl={metadata.logoUrl}
          key={id}
          id={id}
          {...props}
          pkgClassName="pkg stickyAdTrack stickyAdTrack--7up"
          dataTest="sevenUp__section"
        >
          <ThreeUp
            content={{
              ...packageData,
              items: items.slice(0, 3),
              metadata: { ...packageData.metadata, title: null },
            }}
            showFirstImageSource={false}
            {...props}
            pkgClassName="threeUp threeUp--7up"
            isFirstLayout={!layoutIndex}
          />
          <OneUp
            content={{
              ...packageData,
              items: [items[3]],
              metadata: { ...packageData.metadata, title: null },
            }}
            key={id}
            {...props}
            vertical={vertical}
            pkgClassName="one-up one-up--7up"
          />
          <TwoUp
            content={{
              ...packageData,
              items: items.slice(4, 6),
              metadata: { ...packageData.metadata, title: null },
            }}
            {...props}
            vertical={vertical}
            pkgClassName="twoUp twoUp--7up"
          />
          <OneUp
            content={{
              ...packageData,
              items: [items[6]],
              metadata: { ...packageData.metadata, title: null },
            }}
            {...props}
            pkgClassName="one-up one-up--7up"
            vertical={vertical}
          />
        </StickyAdTrack>
      );
    case 'dynamicUp':
      return (
        <DynamicUp
          key={id}
          package={packageData}
          railAdjacent={isRailAdjacent}
          vertical={vertical}
        />
      );

    /** NON-UP PACKAGES */
    case 'ad':
      return (
        <Ad
          key={id}
          packageId={id}
          {...props}
          offsetViewport={slot === 'boxinline' ? 50 : 100}
          pkgClassName="pkg-ad ad mh0 overflow-x-hidden overflow-x-visible-m"
        />
      );
    case 'aLaCarte':
      return (
        <ALaCarte key={id} content={packageData} {...props} />
      );
    case 'bacon':
      return (
        <Bacon
          content={packageData}
          key={id}
          vertical={vertical}
          railAdjacent={isRailAdjacent}
          {...props}
        />
      );
    case 'bigHeadline':
      return (
        <BigHeadline
          content={packageData}
          key={id}
          vertical={vertical}
          {...props}
        />
      );
    case 'bigStoryTakeover':
      return (
        <BigStoryTakeover
          content={packageData}
          key={id}
          {...props}
        />
      );
    case 'collectionBillboard':
      return (
        <Collection
          content={packageData}
          key={id}
          {...props}
          pkgClassName={`pkg collection ${packageData.type}`}
        />
      );
    case 'collectionPoster':
      return (
        <Collection
          content={packageData}
          key={id}
          narrow
          {...props}
          pkgClassName={`pkg collection ${packageData.type}`}
        />
      );
    case 'collectionsLead':
      return (
        <CollectionLead
          key={id}
          content={packageData}
          {...props}
        />
      );
    case 'collectionTitle':
      return <CollectionTitle content={packageData} key={id} alignRight={railContext === 'right'} {...props} />;
    case 'coverSpread':
      switch (packageData.subType) {
        case 'liveBlog':
          return <LiveBlogCoverSpread content={packageData} key={id} {...props} />;
        default:
          return <CoverSpread content={packageData} key={id} {...props} />;
      }
    case 'embed':
      return <Embed content={packageData} key={id} {...props} />;
    case 'leadRectangle':
      return (
        <LeadOneUp
          packageId={id}
          content={items[0]}
          key={id}
          railAdjacent={isRailAdjacent}
          {...props}
        />
      );
    case 'leadSectionFront':
      return (
        <LeadSectionFront
          content={packageData}
          isRailAdjacent={isRailAdjacent}
          key={id}
          {...props}
        />
      );
    // Post List uses LiveBlog component as only live blog content type is supported currently
    case 'postList':
      return <LiveBlog key={id} content={packageData} {...props} />;
    case 'localNews':
      return <LocalNews content={packageData} key={id} {...props} />;
    case 'magazineLead':
      return <MagazineLead packageId={id} content={packageData} {...props} />;
    case 'mosaic':
      return <Mosaic content={packageData} key={id} {...props} />;
    case 'msnbcDaily':
      return <MsnbcDaily content={packageData} key={id} {...props} />;
    case 'pancake':
      return (
        <Pancake
          content={packageData}
          key={id}
          vertical={vertical}
          railAdjacent={isRailAdjacent}
          {...props}
        />
      );
    case 'partnerRecirc':
      return <Taboola content={packageData} key={id} {...props} />;
    case 'promo3Panel':
      return <Promo3Panel content={packageData} key={id} {...props} />;
    case 'slimLead':
      return <SlimLead content={packageData} />;
    case 'showLead':
      return <ShowLead packageId={id} content={packageData} {...props} />;
    case 'standardLead':
      return (
        <StandardLead
          content={packageData}
          isRailAdjacent={isRailAdjacent}
          key={id}
          {...props}
        />
      );
    case 'liveVideoPromo':
      return (
        <LiveVideoPromo
          content={packageData}
          key={id}
          {...props}
        />
      );
    case 'schedule':
      return (
        <Schedule
          content={packageData}
          key={id}
          {...props}
        />
      );
    case 'straightUp':
      return (
        <StraightUp
          content={packageData}
          isRailAdjacent={isRailAdjacent}
          key={id}
          {...props}
        />
      );
    case 'teaseList':
      return (
        <TeaseList
          content={packageData}
          key={id}
          vertical={vertical}
          route={pageRoute}
          {...props}
        />
      );
    case 'threeFeatured':
      return <ThreeFeatured content={packageData} key={id} {...props} />;
    case 'threeFeaturedPlusList':
      return (
        <ThreeFeaturedPlusList
          content={packageData}
          key={id}
          vertical={vertical}
          {...props}
        />
      );
    case 'videoPkg':
      return <VideoPackage content={packageData} key={id} {...props} />;
    case 'waffle':
      switch (packageData.subType) {
        case 'autofilledProductWaffle':
        case 'productWaffle':
          return <ProductWaffle content={packageData} key={id} {...props} />;
        case 'giftGuideWaffle':
          return <GiftGuideWaffle content={packageData} key={id} {...props} />;
        default:
          return (
            <Waffle
              content={packageData}
              key={id}
              {...props}
              adsDisabled={adsDisabled}
            />
          );
      }
    default:
      return null;
  }
}

export function checkForPackage(packages, type, zone = 1) {
  return packages.findIndex((pkg) => pkg.type === type && pkg.zone === zone) > -1;
}

export default function getSafePackage(packageData, props, context) {
  const {
    packageIndex,
    zone,
    zonePackageTypes,
    isRailAdjacent,
    railContext,
  } = context;

  // Full width gets its index in a different way due to Full Bleed splicing.
  // This should be consolidated to be consistent.
  const { packageIndex: packageIndexFromFullWidth } = packageData;

  const pkgComponent = getPackage(packageData, props, context);

  return (
    pkgComponent && (
      <ErrorBoundary key={`ErrorBoundary(${packageData.id})`}>
        <PackageContext
          packageIndex={packageIndexFromFullWidth || packageIndex}
          zone={zone}
          zonePackageTypes={zonePackageTypes}
          isRailAdjacent={isRailAdjacent}
          railContext={railContext}
        >
          {pkgComponent}
        </PackageContext>
      </ErrorBoundary>
    )
  );
}
